import { useCallback, useEffect, useState } from "react";
import posthog from "posthog-js";
import { isConsentRequiredMarket } from "../utility/geo";
import { getStoredConsent, setStoredConsent } from "../utility/consent";
import useAuthentication from "./useAuthentication";

const REOPEN_EVENT = "ngwaahia:open-cookie-preferences";

// Lets any page (e.g. the Privacy Policy "Manage Cookie Preferences" link)
// reopen the banner without a shared React context — avoids provider-
// ordering questions with AuthContextProvider, which this feature doesn't
// otherwise need.
export function requestCookiePreferences() {
  window.dispatchEvent(new Event(REOPEN_EVENT));
}

const useCookieConsent = () => {
  const [consentRequired] = useState(isConsentRequiredMarket);
  const [choice, setChoice] = useState(getStoredConsent);
  const [manuallyOpened, setManuallyOpened] = useState(false);
  const { currentUser } = useAuthentication();

  useEffect(() => {
    const handleReopen = () => setManuallyOpened(true);
    window.addEventListener(REOPEN_EVENT, handleReopen);
    return () => window.removeEventListener(REOPEN_EVENT, handleReopen);
  }, []);

  const accept = useCallback(() => {
    setStoredConsent("granted");
    setChoice("granted");
    posthog.opt_in_capturing();
    // If the visitor logged in before accepting, AuthenticationContext's
    // identify() call was a silent no-op while opted out — re-issue it now
    // so the session gets linked to the user instead of staying anonymous.
    const userId = currentUser?.id || currentUser?._id;
    if (userId) {
      posthog.identify(String(userId), {
        name: currentUser.name,
        email: currentUser.email,
      });
    }
    setManuallyOpened(false);
  }, [currentUser]);

  const reject = useCallback(() => {
    setStoredConsent("denied");
    setChoice("denied");
    posthog.opt_out_capturing();
    setManuallyOpened(false);
  }, []);

  // Closing without choosing counts as a rejection. If they already chose and
  // reopened the banner, closing it just keeps that choice.
  const dismiss = useCallback(() => {
    if (choice === null) {
      reject();
    } else {
      setManuallyOpened(false);
    }
  }, [choice, reject]);

  const reopen = useCallback(() => setManuallyOpened(true), []);

  return {
    consentRequired,
    choice,
    isBannerOpen: consentRequired && (choice === null || manuallyOpened),
    accept,
    reject,
    dismiss,
    reopen,
  };
};

export default useCookieConsent;
